import { createContext, useContext, type Provider } from 'react';

/**
 * Constants for the surfaces that have an active experiment controlling the
 * rovo settings modal. Pass via `<RovoSettingsModalProvider surface={...}>` so
 * the modal can read the correct experiment gate for that surface.
 */
export const RovoSettingsModalSurface = {
	CHAT: 'chat',
	SEARCH_QF_PILL: 'search-qf-connect-apps-pill',
	SEARCH_FPS_CONNECT_APPS_BUTTON: 'search-fps-connect-apps-button',
} as const;

export type RovoSettingsModalSurfaceType =
	(typeof RovoSettingsModalSurface)[keyof typeof RovoSettingsModalSurface];

export type RovoSettingsConfig = {
	/** Product identifier passed as X-product header to the assistance service (e.g. 'confluence', 'jira') */
	product: string;
	siteId: string;
	/**
	 * Organization id of the current tenant. Used to build org-scoped links
	 * (e.g. the shared-chat sender-preview URL, which routes through
	 * `/o/{orgId}`). Optional because not every host has it in context; consumers
	 * fall back to deriving it from the URL when absent.
	 */
	orgId?: string;
	experienceId: string;
	assistanceServiceConfig?: {
		baseUrl?: string;
		headers?: Record<string, string>;
	};
	/** Callback invoked when the user clicks "See what Rovo knows about you" in the Work Profile section. */
	onSeeWhatRovoKnows?: () => void;
	/**
	 * The surface that opened the modal (e.g. 'chat', 'search-qf-connect-apps-pill').
	 * Drives experiment behavior and analytics attribution per surface.
	 */
	surface?: RovoSettingsModalSurfaceType | (string & {});
};

const initialState: RovoSettingsConfig = {
	product: '',
	siteId: '',
	experienceId: 'ai-mate',
	assistanceServiceConfig: undefined,
	surface: undefined,
};

const rovoSettingsConfig = createContext<RovoSettingsConfig>(initialState);
const rovoSettingsModalMountedContext = createContext(false);

export const RovoSettingsConfigProvider: Provider<RovoSettingsConfig> = rovoSettingsConfig.Provider;
export const RovoSettingsModalMountedProvider: Provider<boolean> =
	rovoSettingsModalMountedContext.Provider;
export const useRovoSettingsConfig = (): RovoSettingsConfig => useContext(rovoSettingsConfig);
export const useIsRovoSettingsModalProviderMounted = (): boolean =>
	useContext(rovoSettingsModalMountedContext);
